import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Copy,
  Clock,
  Tag,
  Network,
  Cpu,
  AlertTriangle,
  Timer,
  Terminal,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { useSandboxStore } from '@/stores/useSandboxStore';
import { usePoolStore } from '@/stores/usePoolStore';
import { useAuth } from '@/contexts/AuthContext';
// @ts-expect-error Kept for Phase 2 terminal support — currently unused
import { TerminalContainer } from '@/components/terminal';
import {
  formatShape,
  formatRelativeTime,
  formatDate,
  getTimeRemaining,
  getStateClass,
} from '@/utils/format';
import type { Sandbox } from '@/types';

/** Hook that re-evaluates time-remaining every second while active. */
function useExpirationTimer(expiresAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return useMemo(() => getTimeRemaining(expiresAt), [expiresAt, now]);
}

/** Compute the elapsed fraction for the progress bar (0..1). */
function getLifetimeProgress(createdAt: string, expiresAt: string): number {
  const start = new Date(createdAt).getTime();
  const end = new Date(expiresAt).getTime();
  const total = end - start;
  if (total <= 0) return 1;
  const elapsed = Date.now() - start;
  return Math.min(Math.max(elapsed / total, 0), 1);
}

// ---------------------------------------------------------------------------
// Copyable field helper
// ---------------------------------------------------------------------------

function CopyableField({ label, value }: { label: string; value: string }) {
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Failed to copy');
    }
  }, [label, value]);

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-mono text-xs text-surface-700 dark:text-surface-200 truncate">
        {value}
      </span>
      <button
        onClick={handleCopy}
        className="btn-subtle p-1 flex-shrink-0"
        title={`Copy ${label}`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SandboxDetailPage() {
  const { sandboxId } = useParams<{ sandboxId: string }>();
  const navigate = useNavigate();
  const { getSandboxById, deleteSandbox, fetchSandboxes } = useSandboxStore();
  const { selectedPoolIds } = usePoolStore();
  const selectedPoolId = selectedPoolIds[0] ?? null;
  const { isAdmin } = useAuth();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch sandboxes on mount if list is empty (deep-link scenario)
  const sandboxes = useSandboxStore((s) => s.sandboxes);
  useEffect(() => {
    if (sandboxes.length === 0 && selectedPoolId) {
      fetchSandboxes(selectedPoolId);
    }
  }, [sandboxes.length, selectedPoolId, fetchSandboxes]);

  const sandbox: Sandbox | undefined = sandboxId ? getSandboxById(sandboxId) : undefined;

  // Redirect if sandbox not found (deep-link to invalid ID) — skip during/after delete
  useEffect(() => {
    if (!sandbox && sandboxes.length > 0 && !isDeleting) {
      toast.error('Sandbox not found');
      navigate('/sandboxes');
    }
  }, [sandbox, sandboxes.length, isDeleting, navigate]);

  const remaining = useExpirationTimer(sandbox?.expiresAt);

  // --- Terminate handler ---
  const handleTerminate = useCallback(async () => {
    if (!sandbox || !selectedPoolId) return;
    setIsDeleting(true);
    try {
      await deleteSandbox(selectedPoolId, sandbox.sandboxId);
      toast.success('Sandbox terminated');
      navigate('/sandboxes');
    } catch {
      setIsDeleting(false);
      toast.error('Failed to terminate sandbox');
    }
  }, [sandbox, selectedPoolId, deleteSandbox, navigate]);

  if (!sandbox) {
    return (
      <div className="p-6 lg:p-8">
        <div className="empty-state">
          <div className="spinner" />
          <p className="mt-3 text-sm text-surface-500">Loading...</p>
        </div>
      </div>
    );
  }

  const isRunning = sandbox.state === 'Running';
  const canDelete = sandbox.state === 'Running' || sandbox.state === 'Failed';
  const hasPorts = sandbox.ports && sandbox.ports.length > 0;
  const lifetimeProgress =
    sandbox.expiresAt ? getLifetimeProgress(sandbox.createdAt, sandbox.expiresAt) : null;

  return (
    <div className="p-6 lg:p-8">
      {/* Breadcrumb */}
      <Link
        to="/sandboxes"
        className="inline-flex items-center gap-1 text-xs text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 mb-4 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Sandboxes
      </Link>

      {/* ----------------------------------------------------------------- */}
      {/* Header Card                                                       */}
      {/* ----------------------------------------------------------------- */}
      <div className="card p-4 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Name + state badge */}
            <div className="flex items-center gap-2.5 mb-3">
              <h1 className="text-xl font-semibold text-surface-900 dark:text-surface-100 truncate">
                {sandbox.sandboxName}
              </h1>
              <span className={getStateClass(sandbox.state)}>{sandbox.state}</span>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Network className="w-3.5 h-3.5 text-surface-400" />
                <div>
                  <p className="text-surface-400">Image ID</p>
                  <p className="font-mono text-surface-700 dark:text-surface-200">
                    {sandbox.imageId}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5 text-surface-400" />
                <div>
                  <p className="text-surface-400">Shape</p>
                  <p className="text-surface-700 dark:text-surface-200">
                    {formatShape(sandbox.shape.processorCount, sandbox.shape.memorySize)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-surface-400" />
                <div>
                  <p className="text-surface-400">Pool ID</p>
                  <p className="font-mono text-surface-700 dark:text-surface-200">
                    {sandbox.poolId}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-surface-400" />
                <div>
                  <p className="text-surface-400">Created</p>
                  <p className="text-surface-700 dark:text-surface-200">
                    {formatDate(sandbox.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons (admin only) */}
          {isAdmin && canDelete && (
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              {confirmDelete ? (
                <>
                  <span className="text-xs text-surface-500 mr-1">Are you sure?</span>
                  <button
                    onClick={handleTerminate}
                    className="btn-danger"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Terminate
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Connection + Details Panels (Running only)                        */}
      {/* ----------------------------------------------------------------- */}
      {isRunning && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Connection card */}
          {(sandbox.proxyUri || sandbox.toolUri || hasPorts) && (
            <div className="card">
              <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
                <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-1.5">
                  <Network className="w-4 h-4 text-brand-500" />
                  Connection
                </h2>
              </div>
              <div className="p-4 space-y-2">
                {sandbox.proxyUri && (
                  <div className="flex justify-between items-center gap-2 py-1.5 text-xs">
                    <span className="text-surface-500 dark:text-surface-400 flex-shrink-0">Proxy URI</span>
                    <CopyableField label="Proxy URI" value={sandbox.proxyUri} />
                  </div>
                )}

                {sandbox.toolUri && (
                  <div className="flex justify-between items-center gap-2 py-1.5 text-xs">
                    <span className="text-surface-500 dark:text-surface-400 flex-shrink-0">Tool URI</span>
                    <CopyableField label="Tool URI" value={sandbox.toolUri} />
                  </div>
                )}

                {/* Port mappings table */}
                {hasPorts && (
                  <table className="table w-full text-xs">
                    <thead>
                      <tr>
                        <th>Port</th>
                        <th>Auth Policy</th>
                        <th>Protocol</th>
                        <th>Proxy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sandbox.ports!.map((p) => {
                        const proxyCmd = `w365a sandbox proxy ${sandbox.sandboxId} ${p.port}:${p.port}`;
                        return (
                          <tr key={`${p.port}-${p.protocol}`}>
                            <td className="font-mono">{p.port}</td>
                            <td>{p.policy}</td>
                            <td>{p.protocol}</td>
                            <td>
                              <button
                                onClick={async () => {
                                  await navigator.clipboard.writeText(proxyCmd);
                                  toast.success('Proxy command copied');
                                }}
                                className="btn-subtle inline-flex items-center gap-1 px-1.5 py-0.5 text-xs"
                                title={proxyCmd}
                              >
                                <Terminal className="w-3 h-3" />
                                <Copy className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Details card */}
          <div className="card">
            <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
              <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-brand-500" />
                Details
              </h2>
            </div>
            <div className="p-4 space-y-0">
              <div className="flex justify-between py-1.5 text-xs">
                <span className="text-surface-500 dark:text-surface-400">Sandbox ID</span>
                <CopyableField label="Sandbox ID" value={sandbox.sandboxId} />
              </div>
              <div className="flex justify-between py-1.5 text-xs">
                <span className="text-surface-500 dark:text-surface-400">Pool ID</span>
                <span className="text-surface-900 dark:text-surface-100 font-mono truncate ml-2">
                  {sandbox.poolId}
                </span>
              </div>
              <div className="flex justify-between py-1.5 text-xs">
                <span className="text-surface-500 dark:text-surface-400">Tenant ID</span>
                <span className="text-surface-900 dark:text-surface-100 font-mono truncate ml-2 max-w-[200px]">
                  {sandbox.owner.tenantId}
                </span>
              </div>
              <div className="flex justify-between py-1.5 text-xs">
                <span className="text-surface-500 dark:text-surface-400">Object ID</span>
                <span className="text-surface-900 dark:text-surface-100 font-mono truncate ml-2 max-w-[200px]">
                  {sandbox.owner.objectId}
                </span>
              </div>
              <div className="flex justify-between py-1.5 text-xs">
                <span className="text-surface-500 dark:text-surface-400">Created</span>
                <span className="text-surface-900 dark:text-surface-100">
                  {formatDate(sandbox.createdAt)}
                </span>
              </div>

              {/* Tags as key=value badges */}
              {sandbox.tags && Object.keys(sandbox.tags).length > 0 && (
                <div className="pt-1.5">
                  <span className="text-xs text-surface-500 dark:text-surface-400">Tags</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {Object.entries(sandbox.tags).map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center px-2 py-0.5 text-xs font-mono bg-surface-100 dark:bg-white/[0.06] text-surface-700 dark:text-surface-200 rounded-fluent"
                      >
                        {key}={value}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Termination reason */}
              {sandbox.terminationReason && (
                <div className="flex justify-between py-1.5 text-xs">
                  <span className="text-surface-500 dark:text-surface-400">Termination Reason</span>
                  <span className="text-warning font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {sandbox.terminationReason}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Expiration Card                                                   */}
      {/* ----------------------------------------------------------------- */}
      {sandbox.expiresAt && remaining && (
        <div
          className={clsx('card mb-4', {
            'border-warning/50': remaining.isUrgent,
          })}
        >
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
            <h2
              className={clsx(
                'text-sm font-semibold flex items-center gap-1.5',
                remaining.isUrgent
                  ? 'text-warning'
                  : 'text-surface-900 dark:text-surface-100'
              )}
            >
              {remaining.isUrgent ? (
                <AlertTriangle className="w-4 h-4" />
              ) : (
                <Timer className="w-4 h-4 text-brand-500" />
              )}
              Expiration
            </h2>
          </div>
          <div className="p-4 space-y-3">
            {/* Remaining time prominently */}
            <div className="flex items-baseline gap-2">
              <span
                className={clsx(
                  'text-2xl font-bold',
                  remaining.isUrgent
                    ? 'text-warning'
                    : 'text-surface-900 dark:text-surface-100'
                )}
              >
                {remaining.display}
              </span>
              <span className="text-xs text-surface-500">remaining</span>
            </div>

            {/* Exact expiry */}
            <p className="text-xs text-surface-500">
              Expires at {formatDate(sandbox.expiresAt)}
            </p>

            {/* Progress bar: elapsed / total */}
            {lifetimeProgress !== null && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-surface-500">Lifetime</span>
                  <span className="text-surface-500">
                    {Math.round(lifetimeProgress * 100)}% elapsed
                  </span>
                </div>
                <div className="progress-track">
                  <div
                    className={clsx(
                      remaining.isUrgent ? 'progress-fill-orange' : 'progress-fill-blue'
                    )}
                    style={{ width: `${lifetimeProgress * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Details Sidebar (non-running states)                             */}
      {/* ----------------------------------------------------------------- */}
      {/* TODO: Re-enable terminal for running sandboxes in a future phase.
          The TerminalContainer component is preserved but hidden for now.
      {isRunning && (
        <div className="h-[600px]">
          <TerminalContainer
            sandboxId={sandbox.sandboxId}
            onConnectionError={() => {
              toast.error('Terminal connection lost. Sandbox may have stopped.');
            }}
          />
        </div>
      )} */}
      {!isRunning && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DetailsSidebar sandbox={sandbox} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details Sidebar component
// ---------------------------------------------------------------------------

function DetailsSidebar({ sandbox }: { sandbox: Sandbox }) {
  const hasTags = sandbox.tags && Object.keys(sandbox.tags).length > 0;

  return (
    <div className="space-y-4">
      {/* Tags */}
      {hasTags && (
        <div className="card">
          <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-brand-500" />
              Tags
            </h2>
          </div>
          <div className="p-4 flex flex-wrap gap-1.5">
            {Object.entries(sandbox.tags!).map(([key, value]) => (
              <span
                key={key}
                className="inline-flex items-center px-2 py-0.5 text-xs font-mono bg-surface-100 dark:bg-white/[0.06] text-surface-700 dark:text-surface-200 rounded-fluent"
              >
                {key}={value}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Owner info */}
      <div className="card">
        <div className="px-4 py-3 border-b border-surface-200 dark:border-white/[0.06]">
          <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-surface-400" />
            Details
          </h2>
        </div>
        <div className="p-4 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-surface-500">Tenant ID</span>
            <span className="text-surface-700 dark:text-surface-200 font-mono truncate ml-2 max-w-[200px]">
              {sandbox.owner.tenantId}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-500">Object ID</span>
            <span className="text-surface-700 dark:text-surface-200 font-mono truncate ml-2 max-w-[200px]">
              {sandbox.owner.objectId}
            </span>
          </div>

          {/* Termination reason */}
          {sandbox.terminationReason && (
            <div className="flex justify-between">
              <span className="text-surface-500">Termination Reason</span>
              <span className="text-warning font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {sandbox.terminationReason}
              </span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-surface-500">Created</span>
            <span className="text-surface-700 dark:text-surface-200">
              {formatRelativeTime(sandbox.createdAt)}
            </span>
          </div>

          {/* Sandbox ID (copyable) */}
          <div className="flex items-center justify-between pt-2 border-t border-surface-200 dark:border-white/[0.06]">
            <span className="text-surface-500">Sandbox ID</span>
            <CopyableField label="Sandbox ID" value={sandbox.sandboxId} />
          </div>
        </div>
      </div>
    </div>
  );
}
